import { useEffect, useState } from 'react';
import { Tabs } from '../../components/Tabs';
import {
  answerSuggestion,
  deleteSuggestion,
  listSuggestions,
  setSuggestionStatus,
  type KbSuggestion,
  type SuggestionStatus,
} from '../../lib/assistant-api';
import { knowledgeErrorMessage } from '../../lib/knowledge-api';
import styles from './admin.module.css';
import { confirmDialog } from '../../stores/confirm';

const STATUS_TABS = [
  { id: 'open', label: 'To answer' },
  { id: 'answered', label: 'Answered' },
  { id: 'dismissed', label: 'Dismissed' },
];

const DATE_FMT = new Intl.DateTimeFormat([], { dateStyle: 'medium', timeStyle: 'short' });

/**
 * Suggestions: questions visitors asked that the assistant couldn't answer
 * from the site's data sources, most-asked first. Answering one adds it to the
 * knowledge base as a Q&A, so the next visitor who asks gets the answer.
 */
export function SuggestionsSection({ siteId }: { siteId: string }) {
  const [status, setStatus] = useState<SuggestionStatus>('open');
  const [rows, setRows] = useState<KbSuggestion[] | null>(null);
  const [openCount, setOpenCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [answering, setAnswering] = useState<string | null>(null);

  async function load(which: SuggestionStatus = status) {
    try {
      const res = await listSuggestions(siteId, which);
      setRows(res.suggestions);
      setOpenCount(res.open);
      setError(null);
    } catch {
      setError('Could not load suggestions.');
    }
  }
  useEffect(() => {
    setRows(null);
    void load(status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, status]);

  async function act(fn: () => Promise<unknown>) {
    try {
      await fn();
      await load();
    } catch {
      setError('That didn’t go through. Try again.');
    }
  }

  return (
    <section className={styles.section}>
      <p className={styles.kicker}>Suggestions</p>
      <p className={styles.hint} style={{ marginTop: 0 }}>
        Questions visitors asked that your data sources didn’t cover, or that the assistant wasn’t sure
        about. Answer them here and the answer is added to your data sources as a Q&amp;A, so the next
        visitor gets it. Collected while <strong>Data sources → Use the knowledge base</strong> is on.
      </p>
      <Tabs
        items={STATUS_TABS.map((t) => (t.id === 'open' && openCount ? { ...t, label: `${t.label} (${openCount})` } : t))}
        active={status}
        onChange={(id) => setStatus(id as SuggestionStatus)}
        ariaLabel="Suggestion status"
      />
      {error && <p className={styles.error} role="alert">{error}</p>}
      {rows === null ? (
        <p className={styles.hint}>Loading…</p>
      ) : rows.length === 0 ? (
        <p className={styles.hint}>
          {status === 'open'
            ? 'Nothing to answer: every recent question was covered.'
            : status === 'answered'
              ? 'No answered suggestions yet.'
              : 'No dismissed suggestions.'}
        </p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Question</th>
                <th>Asked</th>
                <th>Last asked</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <SuggestionRow
                  key={s.id}
                  s={s}
                  answering={answering === s.id}
                  onAnswer={() => setAnswering(answering === s.id ? null : s.id)}
                  onSubmitAnswer={async (question, answer) => {
                    await answerSuggestion(siteId, s.id, { question, answer });
                    setAnswering(null);
                    await load();
                  }}
                  onStatus={(next) => act(() => setSuggestionStatus(siteId, s.id, next))}
                  onDelete={async () => {
                    if (await confirmDialog({ title: 'Delete this suggestion?', message: 'It comes back if a visitor asks again.' })) {
                      void act(() => deleteSuggestion(siteId, s.id));
                    }
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function SuggestionRow({
  s,
  answering,
  onAnswer,
  onSubmitAnswer,
  onStatus,
  onDelete,
}: {
  s: KbSuggestion;
  answering: boolean;
  onAnswer: () => void;
  onSubmitAnswer: (question: string, answer: string) => Promise<void>;
  onStatus: (next: SuggestionStatus) => void;
  onDelete: () => void;
}) {
  const [question, setQuestion] = useState(s.question);
  const [answer, setAnswer] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  return (
    <>
      <tr>
        <td>
          {s.question}
          <div className={styles.hint}>
            {s.reason === 'no-match' ? 'No data source matched' : 'The assistant wasn’t sure'}
          </div>
        </td>
        <td>{s.count === 1 ? 'once' : `${s.count} times`}</td>
        <td className={styles.mono}>{DATE_FMT.format(new Date(s.lastSeenAt))}</td>
        <td style={{ whiteSpace: 'nowrap' }}>
          {s.status === 'open' && (
            <>
              <button type="button" className={styles.button} onClick={onAnswer}>
                {answering ? 'Cancel' : 'Answer'}
              </button>{' '}
              <button type="button" className={styles.buttonGhost} onClick={() => onStatus('dismissed')}>
                Dismiss
              </button>
            </>
          )}
          {s.status !== 'open' && (
            <button type="button" className={styles.buttonGhost} onClick={() => onStatus('open')}>
              Reopen
            </button>
          )}{' '}
          <button type="button" className={styles.buttonDanger} onClick={onDelete}>
            delete
          </button>
        </td>
      </tr>
      {answering && (
        <tr>
          <td colSpan={4}>
            <form
              className={styles.form}
              onSubmit={async (e) => {
                e.preventDefault();
                setSaving(true);
                setErr(null);
                try {
                  await onSubmitAnswer(question.trim(), answer.trim());
                } catch (error) {
                  setErr(knowledgeErrorMessage(error) ?? 'Could not save the answer.');
                } finally {
                  setSaving(false);
                }
              }}
            >
              <label className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <span className={styles.label}>Question (tidy it up if you like)</span>
                <input className={styles.input} value={question} maxLength={500} onChange={(e) => setQuestion(e.target.value)} />
              </label>
              <label className={styles.field} style={{ gridColumn: '1 / -1' }}>
                <span className={styles.label}>Answer</span>
                <textarea
                  className={styles.input}
                  rows={4}
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="The answer the assistant should give."
                />
              </label>
              <div className={styles.formActions}>
                <button type="submit" className={styles.button} disabled={saving || !question.trim() || !answer.trim()}>
                  {saving ? 'Saving…' : 'Add to data sources →'}
                </button>
              </div>
              {err && <p className={styles.error}>{err}</p>}
            </form>
          </td>
        </tr>
      )}
    </>
  );
}
